/**
 * The wire shape of /api/v1/credit-notes, mirrored for the page.
 *
 * MIRRORS `api/src/functions/credit-notes/shared/sources/types.ts`. Kept as a hand-written
 * mirror rather than a shared package because that is how every other page in this app does it;
 * the rule is that a field is added HERE only when it exists THERE.
 */

export const CREDIT_NOTE_COMPANIES = ['dbi', 'bin', 'dch', 'dit', 'dsp', 'dfr', 'dpt', 'dtr'] as const;
export type CreditNoteCompany = (typeof CREDIT_NOTE_COMPANIES)[number];

export const CREDIT_NOTE_RULES =
    ['NO_ITEM_LINE', 'DUPLICATE', 'PRICE_DEVIATION', 'LARGE_VS_SALES', 'QTY_NO_VALUE'] as const;
export type CreditNoteRule = (typeof CREDIT_NOTE_RULES)[number];

/**
 * English tile labels. The UI is English; the ERP's own values (reason codes, comment text) and
 * the skip reasons stay in the language they were written in, because translating a stored
 * value is inventing one.
 */
export const RULE_LABELS: Record<CreditNoteRule, string> = {
    NO_ITEM_LINE: 'No item line',
    DUPLICATE: 'Duplicate suspicion',
    PRICE_DEVIATION: 'Price deviation',
    LARGE_VS_SALES: 'Large vs. customer sales',
    QTY_NO_VALUE: 'Qty without value',
};

/** One sentence per rule, shown in the drawer next to the verdict. */
export const RULE_EXPLANATIONS: Record<CreditNoteRule, string> = {
    NO_ITEM_LINE: 'The credit note has no item line at all, so no goods movement stands behind it.',
    DUPLICATE: 'Another credit note with the same customer, amount and currency sits inside the window.',
    PRICE_DEVIATION: 'A credited unit price is further from the invoiced 24-month average than the threshold allows.',
    LARGE_VS_SALES: "The credit note is large against the customer's invoiced turnover for that year.",
    QTY_NO_VALUE: 'A line credits a quantity but carries no value.',
};

export interface CreditNoteRuleOutcome {
    fired: CreditNoteRule[];
    wouldFire: CreditNoteRule[];
    notEvaluated: { rule: CreditNoteRule; reason: string }[];
    signals: {
        priceDeviationPct: number | null;
        salesSharePct: number | null;
        /** The denominator behind salesSharePct — see formatSalesShare for why it is here. */
        customerYearSalesEur: number | null;
        duplicateDocumentNos: string[];
        qtyNoValueLines: number;
    };
}

export interface CreditNoteDocument {
    companyCode: CreditNoteCompany;
    documentNo: string;
    origin: 'erp-header' | 'salestransactions';
    postingDate: string;
    documentDate: string | null;

    customerNr: string | null;
    customerName: string | null;
    hubSpotCustomerUrl: string | null;
    abcClassification: string | null;
    businessUnitCode: string | null;

    currencyCode: string;
    amountDocument: number | null;
    amountEur: number | null;
    amountEurIsExact: boolean;

    erpReasonCodeRaw: string | null;
    erpReasonField: 'GU_Category_BIG' | 'Reason Code' | null;
    erpReasonIsPrefillSource: boolean;

    externalDocumentNo: string | null;
    appliesToDocNo: string | null;
    dwDocumentNo: string | null;
    subject: string | null;
    sourceCode: string | null;
    salespersonCode: string | null;
    systemId: string | null;

    ledgerLinked: boolean;

    itemLineCount: number;
    netSalesEur: number | null;
    grossProfitEur: number | null;
    productCount: number;
    firstProductName: string | null;
    sourceType: string | null;

    rules: CreditNoteRuleOutcome;

    /**
     * What the APP knows about this document — the reason, the two status tracks and whether a
     * recourse case is overdue. Attached server-side over the same rows every P4 filter and
     * every P4 counter reads, so a column, its filter and its number cannot come apart.
     */
    app: CreditNoteAppState;
}

export interface CreditNoteRuleTile {
    ruleCode: CreditNoteRule;
    fired: number;
    wouldFire: number;
    notEvaluated: number;
    notEvaluatedByReason: Record<string, number>;
    enabledFor: string[];
    disabledFor: string[];
    thresholds: Record<string, { value: number | null; unit: string | null; from: string }>;
}

export interface CreditNoteExclusion {
    code: string;
    label: string;
    documents: number;
    documentNos: string[];
}

export interface CreditNoteFreshness {
    lastEtlRunUtc: string | null;
    lastEtlStatus: string | null;
    etlSchedule: string;
    ageInDays: number | null;
    newestPostingDate: Record<string, string | null>;
}

export interface CreditNoteSummary {
    window: { dateFrom: string; dateTo: string };
    companies: string[];
    documentsInWindow: number;
    documentsAfterFilters: number;
    documentsWithoutItemLines: number;
    documentsWithErpReasonCode: number;
    documentsNotInLedger: number;
    documentsFromSalesTransactionsOnly: number;
    byCompany: Record<string, number>;
    byReason: Record<string, number>;
    queryMs: number;
    ruleTiles: CreditNoteRuleTile[];
    exclusions: CreditNoteExclusion[];
    /** German plain text per skip reason, straight from the server. Never invented here. */
    skipReasonLabels: Record<string, string>;

    // ── P4 ──────────────────────────────────────────────────────────────────────────────────
    /** Documents per app reason code over the whole window; 'NONE' = no reason from either side. */
    byAppReason: Record<string, number>;
    byReviewStatus: Record<string, number>;
    /** Keyed by status plus 'NONE' for documents with no case at all. */
    byRecourseStatus: Record<string, number>;
    documentsWithAppReason: number;
    documentsWithReasonFromErp: number;
    documentsWithReasonFromUser: number;
    /** Documents a reviewer may still record a reason on — the work actually left to do. */
    documentsReasonEditableAndEmpty: number;
    /** An ERP code with no catalogue entry: named and counted, never swallowed. */
    unmappedErpReasons: { documents: number; documentNos: string[]; values: string[] };
    /**
     * GROUP-WIDE, NOT SCOPED BY THE PERIOD, and the page says so. A claim does not stop being
     * open because the reader narrowed the window, and this is the same function the Recourse
     * tab summarises itself with — so the KPI and its own click cannot disagree.
     */
    recourse: CreditNoteRecourseSummary;

    // ── P5 ──────────────────────────────────────────────────────────────────────────────────
    /**
     * The Trends tab. Computed by the server from the SAME rows as everything above — a bar's
     * number is what a click on it returns, which is the coupling the rule tiles already have.
     */
    trends: CreditNoteTrends;
}

/**
 * "No reason recorded — neither in the ERP nor here."
 *
 * A CATEGORY, NOT A GAP. It is ranked, stacked and counted like the ten real codes, because how
 * tall that band stands IS the process question this page was built to answer: at dit it is 100 %
 * of the documents and at dch most of them. A chart that dropped those rows would draw an empty
 * picture and call it clean. Must stay in step with `NO_REASON_CODE` in
 * `api/src/functions/credit-notes/shared/trends.ts`.
 */
export const NO_REASON_CODE = 'NONE';

/** One company's answer to "do we know why we credit?" — the process figure of this page. */
export interface CreditNoteCoverageRow {
    companyCode: string;
    documents: number;
    /** Resolved from `GU_Category_BIG` on every read. dbi and bin only. */
    fromErp: number;
    /** Recorded by a person in this app. */
    fromUser: number;
    withoutReason: number;
    /**
     * Documents whose ERP holds a code the app deliberately does NOT map — the BC standard
     * `Reason Code`, which outside dbi and bin is a posting classification and not a reason.
     * dch carries it on 7 of 84 documents in a twelve-month window, so its coverage reading 0 %
     * is a decision, not a gap, and the page says so rather than letting it look like a defect.
     */
    withErpCodeNotMapped: number;
    /** null when the company has no document in the window — 0 % would be a claim about nothing. */
    coveragePct: number | null;
}

export interface CreditNoteTrendReasonRow {
    /** A catalogue code, or 'NONE' — which is a category of its own, never a gap. */
    reasonCode: string;
    /** Straight out of `summary.byAppReason`: the same number the reason filter returns. */
    documents: number;
    valueEur: number;
    /** false when a document in this group had no monthly FX rate, so its value cannot be added. */
    valueEurIsComplete: boolean;
    sortOrder: number;
}

export interface CreditNoteTrendMonth {
    /** 'YYYY-MM'. */
    month: string;
    /** The month in progress: shown, marked, dimmed — not comparable until it closes. */
    isCurrentMonth: boolean;
    documents: number;
    valueEur: number;
    byReason: Record<string, { documents: number; valueEur: number }>;
}

export interface CreditNoteTrends {
    coverageByCompany: CreditNoteCoverageRow[];
    byReason: CreditNoteTrendReasonRow[];
    months: CreditNoteTrendMonth[];
    totals: { documents: number; valueEur: number; valueEurIsComplete: boolean };
}

export interface CreditNoteListResult {
    documents: CreditNoteDocument[];
    summary: CreditNoteSummary;
    freshness: CreditNoteFreshness;
    availableCompanies: string[];
    total: number;
    /** The group-wide reason vocabulary, shipped with the list so the page invents no code. */
    reasonCatalog: CreditNoteReasonCatalogEntry[];
    /** The status vocabularies with their labels, straight from the CHECK constraints. */
    vocabularies: CreditNoteVocabularies;
}

export type CreditNoteLineKind =
    'item' | 'charge' | 'gl' | 'comment' | 'resource' | 'fixedasset' | 'other';

export interface CreditNoteLine {
    lineNo: number;
    kind: CreditNoteLineKind;
    erpLineType: string | null;
    itemNo: string | null;
    description: string | null;
    quantity: number | null;
    unitPrice: number | null;
    amount: number | null;
    unitOfMeasure: string | null;
}

export interface CreditNoteLinesResult {
    companyCode: string;
    documentNo: string;
    lines: CreditNoteLine[];
    commentLines: string[];
    itemLineCount: number;
    queryMs: number;
}

export interface CreditNoteFilters {
    search: string;
    companyCode: string;
    /**
     * The ERP's own reason string. Kept as a parameter the endpoint still honours — a deep link
     * built before P4 keeps working — but the filter bar now offers the APP's group-wide
     * vocabulary instead, which is the one concept that spans all eight companies (decision 2).
     */
    reasonCode: string;
    /** A code from [Reporting].[CreditNoteReasonCatalog], or 'NONE' for "no reason at all". */
    appReasonCode: string;
    reviewStatus: string;
    /** One of the seven, or 'NONE' for a document with no recourse case. */
    recourseStatus: string;
    rule: string;
    dateFrom: string;
    dateTo: string;
}

export const EMPTY_CREDIT_NOTE_FILTERS: CreditNoteFilters = {
    search: '', companyCode: '', reasonCode: '', appReasonCode: '', reviewStatus: '',
    recourseStatus: '', rule: '', dateFrom: '', dateTo: '',
};

/** The stable identity of a credit note across the page, the URL and the drawer. */
export function creditNoteKey(companyCode: string, documentNo: string): string {
    return `${companyCode}-${documentNo}`;
}

/**
 * Split that key back apart.
 *
 * The document number itself contains hyphens at most companies (`SC-CH2600057`,
 * `VNNC26-00023`, `MAN-2512-BRV`), so only the FIRST hyphen separates the two parts. Every
 * company code is three letters and carries no hyphen, which is what makes that safe — and it
 * is the same reason the loader never parses `DocumentID`, whose convention has a known
 * exception. Returns null for anything that is not a well-formed key.
 */
export function parseCreditNoteKey(key: string): { companyCode: string; documentNo: string } | null {
    const at = key.indexOf('-');
    if (at <= 0 || at === key.length - 1) return null;
    return { companyCode: key.slice(0, at), documentNo: key.slice(at + 1) };
}

// ==============================================================================================
// P4 — the write side. Mirrors the P4 block of
// `api/src/functions/credit-notes/shared/sources/types.ts`.
// ==============================================================================================

/** MIRRORS `CK_CNReview_Status` (migration 193) and `CREDIT_NOTE_REVIEW_STATUS` on the server. */
export const CREDIT_NOTE_REVIEW_STATUS =
    ['OPEN', 'IN_REVIEW', 'OK', 'QUESTIONABLE', 'CORRECTED'] as const;
export type CreditNoteReviewStatus = (typeof CREDIT_NOTE_REVIEW_STATUS)[number];

/** MIRRORS `CK_CNRec_Status` (migration 193). */
export const CREDIT_NOTE_RECOURSE_STATUS =
    ['OPEN', 'SUBMITTED', 'ACCEPTED', 'PARTIAL', 'REJECTED', 'SETTLED', 'NOT_PURSUED'] as const;
export type CreditNoteRecourseStatus = (typeof CREDIT_NOTE_RECOURSE_STATUS)[number];

/** MIRRORS `CK_CNRec_Counterparty` (migration 193). */
export const CREDIT_NOTE_COUNTERPARTY_TYPES =
    ['SUPPLIER', 'INSURANCE', 'CARRIER', 'OTHER'] as const;
export type CreditNoteCounterpartyType = (typeof CREDIT_NOTE_COUNTERPARTY_TYPES)[number];

/**
 * A status the page can render but not choose.
 *
 * 'NONE' is the state of a document nobody has opened a recourse case on. It is a real column
 * value in the list — folding it into OPEN would count untouched documents as claims somebody is
 * chasing — but it is not something a case can be set BACK to: once a claim has been opened, it
 * ends as REJECTED or NOT_PURSUED, with its reason, rather than disappearing.
 */
export type CreditNoteRecourseColumnStatus = CreditNoteRecourseStatus | 'NONE';

/**
 * A vocabulary entry as the server sends it. The labels come down the wire rather than being
 * kept here, for the same reason the German skip labels do: one source, so a value added on the
 * server can never render as a raw code on the page.
 */
export interface CreditNoteVocabularyEntry<T extends string = string> {
    code: T;
    label: string;
}

export interface CreditNoteVocabularies {
    reviewStatuses: CreditNoteVocabularyEntry<CreditNoteReviewStatus>[];
    recourseStatuses: CreditNoteVocabularyEntry<CreditNoteRecourseStatus>[];
    counterpartyTypes: CreditNoteVocabularyEntry<CreditNoteCounterpartyType>[];
}

/** One row of [Reporting].[CreditNoteReasonCatalog]. */
export interface CreditNoteReasonCatalogEntry {
    reasonCode: string;
    /** NULL for all ten today — open question 1, with Stefan. */
    reasonLabelDe: string | null;
    reasonLabelEn: string | null;
    /** The ERP's own 20-character stub, umlauts included. */
    erpCategoryRaw: string | null;
    sortOrder: number;
    isActive: boolean;
}

/**
 * What to show for a reason code.
 *
 * NO LABEL IS INVENTED HERE. The catalogue's long texts are Stefan's to write and are NULL for
 * all ten codes, so the ERP's own stub is what a reader sees — `8 H CLAIM SCHADENERS` is the
 * whole stored value, truncated in the ERP itself and not by this page. Guessing that it means
 * "damage compensation" would be the app putting words in the business's mouth, which decision 2
 * reserves for the business. The code itself is the last resort, for an app-only entry with no
 * ERP string behind it.
 */
export function reasonLabel(entry: CreditNoteReasonCatalogEntry | undefined, code?: string): string {
    if (!entry) return code ?? '–';
    return entry.reasonLabelEn || entry.reasonLabelDe || entry.erpCategoryRaw || entry.reasonCode;
}

export interface CreditNoteReasonState {
    reasonCode: string;
    source: 'ERP' | 'USER';
    erpCategoryRaw: string | null;
    comment: string | null;
    updatedAt: string | null;
    updatedBy: string | null;
}

export interface CreditNoteReviewState {
    status: CreditNoteReviewStatus;
    changeReason: string | null;
    reviewDueAt: string | null;
    /** True when no row exists yet — Open by absence, not because anybody set it. */
    isDefault: boolean;
    updatedAt: string | null;
    updatedBy: string | null;
}

export interface CreditNoteRecourseCase {
    companyCode: string;
    documentNo: string;
    caseNo: number;
    status: CreditNoteRecourseStatus;
    counterpartyType: CreditNoteCounterpartyType;
    counterpartyName: string | null;
    vendorId: string | null;
    claimedAmount: number | null;
    claimedCurrency: string | null;
    receivedAmount: number | null;
    receivedCurrency: string | null;
    submittedOn: string | null;
    settledOn: string | null;
    reviewDueAt: string | null;
    hubSpotTicketId: string | null;
    notes: string | null;
    changeReason: string | null;
    isOverdue: boolean;
    daysOverdue: number | null;
    createdAt: string | null;
    createdBy: string | null;
    updatedAt: string | null;
    updatedBy: string | null;
}

export interface CreditNoteHistoryEntry {
    historyId: number;
    track: 'REVIEW' | 'RECOURSE' | 'REASON';
    caseNo: number | null;
    fieldName: string;
    oldValue: string | null;
    newValue: string | null;
    changeReason: string | null;
    changedBy: string;
    changedAt: string;
}

/** The app's own state for one document, attached to every list row by the server. */
export interface CreditNoteAppState {
    reason: CreditNoteReasonState | null;
    /**
     * False exactly when this document's ERP carries `GU_Category_BIG` (decision 2). Per
     * DOCUMENT, not per company: 92 dbi and 15 bin credit notes from before February 2025 carry
     * no code and are as unrecorded as any dch document.
     *
     * The form disables itself from this flag; the endpoint refuses regardless. A lock that only
     * exists as a disabled input is not a lock.
     */
    reasonEditable: boolean;
    /** The ERP carries a code that maps to no catalogue entry — an eleventh value in BC. */
    erpReasonUnmapped: boolean;
    /**
     * A code a reviewer recorded that the ERP has since overruled. The ERP keeps the last word
     * (decision 2); this is what stops the earlier decision from vanishing silently.
     */
    supersededUserReason: string | null;
    review: CreditNoteReviewState;
    recourse: CreditNoteRecourseCase[];
    recourseStatus: CreditNoteRecourseColumnStatus;
    recourseOverdue: boolean;
}

/** The recourse counters, from ONE server-side function shared by the KPI and the queue. */
export interface CreditNoteRecourseSummary {
    openCases: number;
    openBySupplier: number;
    openByInsurance: number;
    totalCases: number;
    /** Per currency, never added across them — a CHF claim can be paid in EUR. */
    expectedByCurrency: Record<string, number>;
    receivedByCurrency: Record<string, number>;
    overdueCases: number;
    oldestOverdueDays: number | null;
    settledCases: number;
    notPursuedCases: number;
}

/** One row of the recourse queue: the app's case plus the ERP facts about the credit note. */
export interface CreditNoteRecourseRow extends CreditNoteRecourseCase {
    postingDate: string | null;
    customerNr: string | null;
    customerName: string | null;
    creditedEur: number | null;
    creditedCurrency: string | null;
    creditedDocument: number | null;
    hubSpotCustomerUrl: string | null;
    /** False when the credit note is older than the server's enrichment window. */
    documentInWindow: boolean;
}

export interface CreditNoteRecourseQueue {
    cases: CreditNoteRecourseRow[];
    summary: CreditNoteRecourseSummary & {
        casesAfterFilters: number;
        outsideEnrichmentWindow: { cases: number; documentNos: string[]; windowMonths: number };
        byCompany: Record<string, number>;
        queryMs: number;
    };
    vocabularies: Omit<CreditNoteVocabularies, 'reviewStatuses'> & { openStatuses: string[] };
    availableCompanies: string[];
}

export interface CreditNoteRecourseFilters {
    search: string;
    status: string;
    counterpartyType: string;
    company: string;
    overdueOnly: boolean;
    openOnly: boolean;
}

export const EMPTY_RECOURSE_FILTERS: CreditNoteRecourseFilters = {
    search: '', status: '', counterpartyType: '', company: '', overdueOnly: false, openOnly: false,
};

/** Everything the app knows about one document — the drawer's payload, and every write's answer. */
export interface CreditNoteDetail {
    companyCode: string;
    documentNo: string;
    reason: CreditNoteReasonState | null;
    reasonEditable: boolean;
    erpReasonUnmapped: boolean;
    supersededUserReason: string | null;
    review: CreditNoteReviewState;
    recourse: CreditNoteRecourseCase[];
    history: CreditNoteHistoryEntry[];
    catalog: CreditNoteReasonCatalogEntry[];
    erpReasonRaw: string | null;
    erpReasonIsPrefillSource: boolean;
    vocabularies: CreditNoteVocabularies;
    /**
     * Which fields a write actually moved. Present only on a write's response; empty means the
     * save was legal and changed nothing, which the page says rather than reporting a success
     * that left no trace in the trail.
     */
    changedFields?: string[];
    queryMs?: number;
}

export interface ReasonWritePayload {
    company: string;
    documentNo: string;
    reasonCode: string;
    comment?: string | null;
    changeReason: string;
}

export interface ReviewWritePayload {
    company: string;
    documentNo: string;
    status: CreditNoteReviewStatus;
    reviewDueAt?: string | null;
    changeReason: string;
}

export interface RecourseWritePayload {
    company: string;
    documentNo: string;
    caseNo?: number;
    status: CreditNoteRecourseStatus;
    counterpartyType: CreditNoteCounterpartyType;
    counterpartyName?: string | null;
    vendorId?: string | null;
    claimedAmount?: number | string | null;
    claimedCurrency?: string | null;
    receivedAmount?: number | string | null;
    receivedCurrency?: string | null;
    submittedOn?: string | null;
    settledOn?: string | null;
    reviewDueAt?: string | null;
    hubSpotTicketId?: string | null;
    notes?: string | null;
    changeReason: string;
}
